import React from 'react';
import { Truck, Users, Sparkles } from 'lucide-react';
import { businessInfo } from '../data/mockData';

export const About = () => {
  const features = [
    {
      icon: Truck,
      title: "Mobile Excellence",
      description: "Our fully equipped food truck brings restaurant-quality Indian street food directly to your location."
    },
    {
      icon: Sparkles,
      title: "Fusion Flavors",
      description: "Traditional Indian recipes meet modern twists, creating unforgettable taste experiences."
    },
    {
      icon: Users,
      title: "Community Focused",
      description: "We're passionate about bringing people together through authentic, delicious food."
    }
  ];

  return (
    <section id="about" className="relative py-24 bg-gradient-to-b from-orange-50 via-yellow-50 to-white overflow-hidden">
      {/* Decorative Background */}
      <div className="absolute top-0 right-0 w-1/3 h-1/3 bg-gradient-to-br from-orange-200/40 to-yellow-200/40 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-1/4 h-1/4 bg-gradient-to-tr from-emerald-200/30 to-green-200/30 rounded-full blur-3xl"></div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left: Image */}
          <div className="relative">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img 
                src={businessInfo.truckImage}
                alt="Desi Fusion Food Truck"
                className="w-full h-[500px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-burgundy-900/70 to-transparent"></div>
              <div className="absolute bottom-8 left-8 right-8">
                <div className="bg-white/95 backdrop-blur-sm rounded-xl p-10 shadow-2xl border-2 border-orange-200">
                  <p className="text-5xl font-bold bg-gradient-to-r from-burgundy-800 to-red-700 bg-clip-text text-transparent mb-3">Desi Fusion</p>
                  <p className="text-orange-600 font-semibold text-3xl">{businessInfo.tagline}</p>
                </div>
              </div>
            </div>
            {/* Decorative accent */}
            <div className="absolute -top-6 -right-6 w-24 h-24 bg-gradient-to-br from-orange-400 to-yellow-400 rounded-full opacity-30 blur-xl"></div>
            <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-gradient-to-br from-emerald-400 to-green-400 rounded-full opacity-30 blur-xl"></div>
          </div>

          {/* Right: Content */}
          <div>
            <div className="inline-block mb-4">
              <span className="text-orange-600 font-bold text-sm uppercase tracking-wider bg-gradient-to-r from-orange-100 to-yellow-100 px-4 py-2 rounded-full border border-orange-200">
                About Us
              </span>
            </div>
            
            <h2 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-burgundy-800 via-red-700 to-orange-700 bg-clip-text text-transparent mb-6 leading-tight">
              Bringing Authentic Indian Flavors to Your Doorstep
            </h2>
            
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              {businessInfo.description}
            </p>
            
            <p className="text-lg text-gray-700 mb-8 leading-relaxed">
              From butter chicken bowls to crispy samosas and aromatic chai, every dish is crafted with passion, premium ingredients, and authentic spices. Whether it's a wedding, corporate event, or local festival, we're committed to making your experience unforgettable.
            </p>

            {/* Features */}
            <div className="space-y-6">
              {features.map((feature, index) => (
                <div 
                  key={index}
                  className="flex items-start space-x-4 group"
                >
                  <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-orange-500 via-orange-600 to-red-600 rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                    <feature.icon className="text-white" size={24} />
                  </div>
                  <div>
                    <h3 className="font-bold text-burgundy-900 text-lg mb-1">{feature.title}</h3>
                    <p className="text-gray-600">{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
